import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIsSpeaking, useLocalParticipant } from '@livekit/components-react';
import {
  Bar,
  CSS_EASINGS,
  VisualiserContainer,
  BASE_HEIGHT,
  STOP_ANIMATION_DURATION,
} from './styles';

type AudioVisualiserProps = {
  barCount?: number;
};

// Animation constants
const EXTRA_HEIGHT = 0.85; // Maximum additional scale
const BASE_DURATION = 250; // Base animation duration in ms
const STOP_GRACE_MS = 120; // ignore brief silences (VAD gaps) within this time
const MAX_DELAY_OFFSET = 100; // Maximum random delay in ms
const DURATION_VARIATION_RANGE = 80; // +/- 40ms variation range
const AMPLITUDE_MIN = 0.8; // Minimum amplitude variation multiplier
const AMPLITUDE_RANGE = 0.4; // Range for amplitude variation (0.8-1.2x)
const SENSITIVITY_REDUCTION_FACTOR = 0.6; // Factor to reduce sensitivity for outer bars

function getScaleYFromMatrix(matrix: string): number {
  if (matrix.startsWith('matrix3d(')) {
    const vals = matrix.slice(9, -1).split(',').map(Number);

    return vals[5] || 1;
  }
  if (matrix.startsWith('matrix(')) {
    const vals = matrix.slice(7, -1).split(',').map(Number);

    return vals[3] || 1;
  }

  return 1;
}

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function nextPeakScale(sensitivity: number) {
  const amplitude = AMPLITUDE_MIN + Math.random() * AMPLITUDE_RANGE;
  const scale = BASE_HEIGHT + EXTRA_HEIGHT * sensitivity * amplitude;

  return Math.min(1, scale);
}

function nextDuration() {
  const delta = (Math.random() - 0.5) * DURATION_VARIATION_RANGE;

  return Math.max(80, Math.round(BASE_DURATION + delta));
}

function AudioVisualiserBar({
  isSpeaking,
  sensitivity,
}: {
  isSpeaking: boolean;
  sensitivity: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);

  const prefersReducedMotion = useMemo(
    () =>
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    [],
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion) return;

    // resume animation
    if (isSpeaking && pausedRef.current) {
      el.style.removeProperty('animation');
      el.style.removeProperty('animation-name');
      el.style.removeProperty('animation-play-state');
      el.style.removeProperty('transition');
      el.style.removeProperty('transform');
      pausedRef.current = false;

      return;
    }

    if (pausedRef.current || isSpeaking) return;

    // pause & glide to base
    const computed = getComputedStyle(el).transform;
    const snapshotScale = getScaleYFromMatrix(computed) || BASE_HEIGHT;

    el.style.animationPlayState = 'paused';
    el.style.animationName = 'none';
    pausedRef.current = true;

    el.style.transform = `scaleY(${snapshotScale})`;
    requestAnimationFrame(() => {
      el.style.transition = `transform ${STOP_ANIMATION_DURATION}ms ease`;
      el.style.transform = `scaleY(${BASE_HEIGHT})`;
    });

    const handleEnd = () => {
      el.style.removeProperty('transition');
    };
    el.addEventListener('transitionend', handleEnd);

    return () => el.removeEventListener('transitionend', handleEnd);
  }, [isSpeaking, prefersReducedMotion]);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion) return;

    const onIter = () => {
      if (!isSpeaking) return;
      el.style.setProperty('--max-scale', String(nextPeakScale(sensitivity)));
    };

    el.style.setProperty('--max-scale', String(nextPeakScale(sensitivity)));
    el.addEventListener('animationiteration', onIter);

    return () => el.removeEventListener('animationiteration', onIter);
  }, [isSpeaking, sensitivity, prefersReducedMotion]);

  const { durationMs, delayMs, timing } = useMemo(
    () => ({
      durationMs: nextDuration(),
      delayMs: Math.round(Math.random() * MAX_DELAY_OFFSET),
      timing: pick(CSS_EASINGS),
    }),
    [],
  );

  return (
    <Bar
      ref={ref}
      $durationMs={durationMs}
      $delayMs={delayMs}
      $timing={timing}
    />
  );
}

export function AudioVisualiser({ barCount = 5 }: AudioVisualiserProps) {
  const { localParticipant } = useLocalParticipant();
  const rawSpeaking = useIsSpeaking(localParticipant) || false;
  const [smoothedSpeaking, setSmoothedSpeaking] = useState(rawSpeaking);

  useEffect(() => {
    if (rawSpeaking) {
      setSmoothedSpeaking(true);

      return;
    }

    const t = window.setTimeout(
      () => setSmoothedSpeaking(false),
      STOP_GRACE_MS,
    );

    return () => window.clearTimeout(t);
  }, [rawSpeaking]);

  const bars = useMemo(
    () =>
      Array.from({ length: barCount }, (_, i) => ({
        key: `bar-${i}`,
        sensitivity:
          1 -
          (Math.abs(i - (barCount - 1) / 2) / ((barCount - 1) / 2 || 1)) *
            SENSITIVITY_REDUCTION_FACTOR,
      })),
    [barCount],
  );

  return (
    <VisualiserContainer aria-hidden="true">
      {bars.map(({ key, sensitivity }) => (
        <AudioVisualiserBar
          key={key}
          isSpeaking={smoothedSpeaking}
          sensitivity={sensitivity}
        />
      ))}
    </VisualiserContainer>
  );
}
